import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Globe, Lock } from 'lucide-react';

export default function DomainSettingsModal({
  isOpen,
  onClose,
  team,
  onAddDomain,
  onRemoveDomain,
  onToggleRestriction,
}) {
  const [newDomain, setNewDomain] = useState('');
  const [msg, setMsg] = useState('');
  if (!isOpen) return null;

  const handleAddDomain = async e => {
    e.preventDefault();
    setMsg('');
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return setMsg('Please enter a domain.');

    try {
      await onAddDomain(domain);
      setNewDomain('');
    } catch (err) {
      setMsg(err?.message || 'Failed to add domain.');
    }
  };

  const handleRemoveDomain = async domain => {
    try {
      await onRemoveDomain(domain);
    } catch (err) {
      setMsg(err?.message || 'Failed to remove domain.');
    }
  };

  const handleToggleRestriction = async allowAny => {
    try {
      await onToggleRestriction(allowAny);
    } catch (err) {
      setMsg(err?.message || 'Failed to update domain settings.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-[92%] max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:w-full md:max-w-lg md:p-6"
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-semibold">Domain Settings</h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <h4 className="mb-3 text-lg font-medium">Access Control</h4>
          <div className="space-y-3">
            <button
              onClick={() => handleToggleRestriction(false)}
              className={`flex w-full items-center justify-between rounded-xl border p-4 transition-colors ${
                !team?.allowAnyDomain
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">Restrict to specific domains</div>
                  <div className="text-sm text-slate-600">
                    Only allow emails from specified domains
                  </div>
                </div>
              </div>
              {!team?.allowAnyDomain && <div className="h-3 w-3 rounded-full bg-blue-500"></div>}
            </button>

            <button
              onClick={() => handleToggleRestriction(true)}
              className={`flex w-full items-center justify-between rounded-xl border p-4 transition-colors ${
                team?.allowAnyDomain
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Globe className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">Allow any domain</div>
                  <div className="text-sm text-slate-600">Allow emails from any domain</div>
                </div>
              </div>
              {team?.allowAnyDomain && <div className="h-3 w-3 rounded-full bg-blue-500"></div>}
            </button>
          </div>
        </div>

        {!team?.allowAnyDomain && (
          <div className="mb-6">
            <h4 className="mb-3 text-lg font-medium">Allowed Domains</h4>

            <form onSubmit={handleAddDomain} className="mb-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="flex-1 rounded-xl border px-4 py-2 outline-none focus:ring-2 focus:ring-slate-600"
                  placeholder="Enter domain (e.g., company.com)"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                />
                <button
                  type="submit"
                  className="flex items-center space-x-2 rounded-xl bg-slate-800 px-4 py-2 text-white hover:opacity-90"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add</span>
                </button>
              </div>
            </form>

            <div className="space-y-2">
              {team?.allowedDomains?.map(domain => (
                <div
                  key={domain}
                  className="flex items-center justify-between rounded-lg border bg-slate-50 px-3 py-2"
                >
                  <span className="font-medium">{domain}</span>
                  <button
                    onClick={() => handleRemoveDomain(domain)}
                    className="rounded-full p-1 text-red-500 hover:bg-red-100"
                    title="Remove domain"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {(!team?.allowedDomains || team.allowedDomains.length === 0) && (
                <div className="rounded-lg border border-dashed p-4 text-center text-slate-500">
                  No domains added yet. Add domains to restrict access.
                </div>
              )}
            </div>
          </div>
        )}

        {msg && <p className="mb-4 text-sm text-red-600">{msg}</p>}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl bg-slate-800 px-4 py-2 font-medium text-white hover:opacity-90"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
}
